import { TabPanel } from 'devextreme-react';
import { confirm } from 'devextreme/ui/dialog';
import { equals } from 'ramda';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { putApi } from '../../apiUtils';
import { CustomerAssetsDataGrid } from '../../components/datagrid/CustomerAssetsDataGrid';
import { CustomerProjectsDataGrid } from '../../components/datagrid/CustomerProjectsDataGrid';
import { useBreadcrumbs } from '../../contexts/breadcrumbsContext';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { CustomersForm } from '../../forms/customers/CustomersForm';
import { PageContentWrapper } from '../../layouts/contentWrapper/PageContentWrapper';
import { CustomerAsset } from '../../types/customerAsset.types';
import { CustomerProject } from '../../types/customerProject.types';
import { DetailPageParams } from '../../types/pageParams.types';
import { UnitInputs } from '../../types/unit.types';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { swrConfig } from '../Assets/AssetsEdit';

export function CustomersEdit() {
  const { translate } = useTranslation();
  const transFormName = 'Customer.Update';
  const history = useHistory();

  const { id } = useParams<DetailPageParams>();
  const { startLoading, stopLoading } = useLoading();
  const { setValue } = useBreadcrumbs();
  const [currentTab, setCurrentTab] = useState<number>(0);

  useEffect(() => {
    setValue({
      pageName: 'Edit customer',
      steps: [
        { name: 'Home', pathToCutCount: 3 },
        { name: 'Customers', pathToCutCount: 2 },
      ],
    });
    return () => {
      setValue(null);
    };
  }, []);

  const { data: formData, error } = useSWR<UnitInputs>(
    `${API_URL.CUSTOMER_ITEM}?id=${id}`,
    fetcher,
  );

  const { data: projects } = useSWR<CustomerProject[]>(
    `${API_URL.CUSTOMER_PROJECT_LIST}?visibleOnly=false&parentId=${Number(id)}`,
    fetcher,
    swrConfig,
  );

  const { data: assets, mutate: refetchAssets } = useSWR<CustomerAsset[]>(
    `${API_URL.ASSET_LIST_ASSIGN_TO_CUSTOMER}?visibleOnly=false&customerId=${Number(id)}`,
    fetcher,
    swrConfig,
  );

  const onSubmit = async (e) => {
    startLoading();
    if (e !== null) e.preventDefault();
    let isOk = false;
    await putApi({
      url: API_URL.CUSTOMER_PUT,
      data: formData,
      callAfterSuccess: () => {
        isOk = true;
      },
    });
    stopLoading();
    return isOk;
  };

  const handleEditCustomerProjectButton = (e): void => {
    history.push(`/customers-projects/edit/${e.row.data.id}`);
  };

  const handleDeleteCustomerAssetButton = useCallback(async (e: any) => {
    if (
      !(await confirm(
        translate!(`Are you sure you want to unassign asset from customer?`, 'CustomerAssets.List'),
        translate!('Confirm', ''),
      ))
    )
      return;

    await putApi({
      url: `${API_URL.ASSET_UNASSIGN_TO_CUSTOMER}?assetId=${e.row.data.id}&customerId=${id}`,
      data: {},
      callAfterSuccess: () => {
        refetchAssets();
      },
    });
  }, []);

  const tabs = [
    {
      title: translate!('Projects', transFormName),
      index: 0,
      component: equals(currentTab, 0) && (
        <CustomerProjectsDataGrid
          data={projects}
          handleEdit={handleEditCustomerProjectButton}
          customerId={Number(id)}
        />
      ),
    },
    {
      title: translate!('Assets', transFormName),
      index: 1,
      component: equals(currentTab, 1) && (
        <CustomerAssetsDataGrid
          data={assets}
          handleDelete={handleDeleteCustomerAssetButton}
          customerId={Number(id)}
          onAddSuccess={() => refetchAssets()}
        />
      ),
    },
  ];

  return (
    <PageContentWrapper
      error={error}
      loading={!formData}
      heading={translate!('Edit customer', transFormName)}
    >
      <CustomersForm onSubmit={onSubmit} formData={formData} isEdit />
      <div style={{ marginTop: '20px' }}>
        <TabPanel
          selectedIndex={currentTab}
          dataSource={tabs}
          itemRender={(a) => a.component}
          activeStateEnabled={true}
          onTitleClick={(e: any) => setCurrentTab(e.itemIndex)}
          deferRendering={false}
          focusStateEnabled={false}
        />
      </div>
    </PageContentWrapper>
  );
}

export default CustomersEdit;
